import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BaseForm } from '@app/components/common/forms/BaseForm/BaseForm';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { doLogin, doSwipe } from '@app/store/slices/authSlice';
import { notificationController } from '@app/controllers/notificationController';
import * as S from './LoginForm.styles';
import * as Auth from '@app/components/layouts/AuthLayout/AuthLayout.styles';
import { Form, InputRef, notification, Spin } from 'antd';
import FocusLock from 'react-focus-lock';

interface LoginFormData {
  username: string;
  password: string;
}

export const initValues: LoginFormData = {
  username: '',
  password: '',
};

interface AccessCardData {
  cardData: string;
}

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isModalVisible, setModalVisible] = useState(false);
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const [isAccessing, setIsAccessing] = useState(false);
  const [cardData, setCardData] = useState('');
  const [cardForm] = Form.useForm();
 
  const usernameInputRef = useRef<InputRef | null>(null);
  const cardInputRef = useRef<InputRef | null>(null);



  const handleSubmit = (values: LoginFormData) => {
    setLoading(true);
    dispatch(doLogin(values))
      .unwrap()
      .then(() => navigate('/'))
      .catch((err) => {
        notificationController.error({ message: err.message });
        setLoading(false);
      });
  };

  const handleCardAccess = () => {
    setModalVisible(true);
    if (cardInputRef.current) {
      cardInputRef.current.focus();
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    cardForm.resetFields(['cardData']);
  };

  const handleCardSubmit = (values: AccessCardData) => {
    setIsAccessing(true);
    dispatch(doSwipe(values))
      .unwrap()
      .then(() => navigate('/'))
      .catch((err) => {
        notificationController.error({ message: err.message });
        cardForm.resetFields(['cardData']);
        
      }).finally(() => setIsAccessing(false));
  };

  useEffect(() => {
    const handleCardSwipe = (event: KeyboardEvent) => {
      if (isModalVisible) {
        if (event.key === 'Enter') {
          // Trigger the API call when 'Enter' is pressed
         
          if (cardData) {
            setIsAccessing(true);
            dispatch(doSwipe({ cardData }))
              .unwrap()
              .then(() => {
                navigate('/'); // Navigate on success
              })
              .catch((err) => {
                notificationController.error({ message: err.message });
              })
              .finally(() => {
                setCardData(''); // Clear card data after submission
                setIsAccessing(false);
              });
          }
        } else {
          // Accumulate swipe data
          setCardData((prev) => prev + event.key);
        }
      }
    };

    window.addEventListener('keypress', handleCardSwipe);

    return () => {
      window.removeEventListener('keypress', handleCardSwipe);
    };
  }, [cardData, isModalVisible, dispatch, navigate]);

  return (
    <Auth.FormWrapper>
      {
        isModalVisible ? (
          <>
            <BaseForm layout="vertical">
              <Auth.FormTitle>{t('common.cardAccess')}</Auth.FormTitle>
              <FocusLock>
                <Spin spinning={isAccessing} tip={t('common.accessing')}>
                  <p>{t('common.swipeCardMessage')}</p>
                    <Auth.FormItem>
                      <Auth.SubmitButton type="primary" htmlType="button" onClick={handleModalClose} loading={isLoading}>
                        {t('common.cancelAccessWithCard')}
                      </Auth.SubmitButton>
                    </Auth.FormItem>
                </Spin>
              </FocusLock>
            </BaseForm>
          </>
        ) : (
          <>
            <BaseForm layout="vertical" onFinish={handleSubmit} requiredMark="optional" initialValues={initValues}>
              <Auth.FormTitle>{t('common.login')}</Auth.FormTitle>
              <S.LoginDescription>{t('login.loginInfo')}</S.LoginDescription>
              <Auth.FormItem
                name="username"
                label={t('common.username')}
                rules={[
                  { required: true, message: t('common.requiredField') },
                ]}
              >
              <Auth.FormInput placeholder={t('common.username')} ref={(input) => {
                usernameInputRef.current = input;
              }} />
              </Auth.FormItem>
              <Auth.FormItem
                label={t('common.password')}
                name="password"
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <Auth.FormInputPassword placeholder={t('common.password')} />
              </Auth.FormItem>
              <BaseForm.Item>
                <Auth.SubmitButton type="primary" htmlType="submit" loading={isLoading}>
                  {t('common.login')}
                </Auth.SubmitButton>
              </BaseForm.Item>
               {process.env.REACT_APP_CURRENT_LOCALITY === 'PT' && (
                <BaseForm.Item>
                  <Auth.SubmitButton type="default" htmlType="button" onClick={handleCardAccess} loading={isLoading}>
                    {t('common.accessWithCard')}
                  </Auth.SubmitButton>
                </BaseForm.Item>
              )}
            </BaseForm>

          </>
        )
      }
    </Auth.FormWrapper>
  );
};
